import { Response, SnaphotReference } from '@/models'
import axios from 'axios'
import CONSTANTS from '@/constants/Config.constants'
import { Chart, ChartDataProperties } from '@/models/chart.model'
import {
  ChartDetails,
  AvailableWidgets,
  DashboardResource,
  ParsedConfiguration,
  UnparsedDashboardResource,
} from '@/models/dashboard.model'
import { IDashboardService } from '@/services/IDashboardService'
import { AnalyzeTableHeader } from '@/models/table.model'
import { useEnvStore } from '@/stores/envStore'

const envStore = useEnvStore()
function parseConfiguration(configuration: string): ParsedConfiguration {
  try {
    return JSON.parse(configuration)
  } catch {
    return {}
  }
}

function serializeDashboardResource(dashboard: DashboardResource): UnparsedDashboardResource {
  return {
    ...dashboard,
    widgets: dashboard.widgets.map((widget) => ({
      ...widget,
      configuration: JSON.stringify(widget.configuration),
    })),
  }
}

function deserializeDashboardResource(dashboard: UnparsedDashboardResource): DashboardResource {
  return {
    ...dashboard,
    widgets: dashboard.widgets.map((widget) => ({
      ...widget,
      configuration: parseConfiguration(widget.configuration),
    })),
  }
}

export default class DashboardService implements IDashboardService {
  private readonly API_URL: string = envStore.variables.ANALYZE_API_BACKEND_URL
  private axiosConfig = {
    headers: {
      'Content-Type': CONSTANTS.CONTENT_TYPES.YT_V1,
    },
    data: null,
  }

  public async getAvailableWidgets(
    projectId: number,
    snapshotId: SnaphotReference,
    filter?: number,
  ): Promise<AvailableWidgets> {
    const params = { filter }
    return axios
      .get<Response<AvailableWidgets>>(
        `${this.API_URL}/project/${projectId}/snapshot/${snapshotId}/dashboard/widgets`,
        { ...this.axiosConfig, params },
      )
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async createDashboard(
    projectId: number,
    dashboard: Omit<DashboardResource, 'id'>,
  ): Promise<DashboardResource> {
    const data = serializeDashboardResource({ ...dashboard, id: Number.MAX_SAFE_INTEGER })

    return axios
      .post<Response<DashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard`,
        { data },
        this.axiosConfig,
      )
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async getDashboards(projectId: number): Promise<DashboardResource[]> {
    return axios
      .get<Response<UnparsedDashboardResource[]>>(
        `${this.API_URL}/project/${projectId}/dashboard`,
        this.axiosConfig,
      )
      .then((response) => response.data.data.map(deserializeDashboardResource))
      .catch((error) => Promise.reject(error))
  }

  public async getDashboard(
    projectId: number,
    dashboardId: number | undefined,
  ): Promise<DashboardResource> {
    return axios
      .get<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboardId ?? 0}`,
        this.axiosConfig,
      )
      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async updateDashboard(
    projectId: number,
    dashboard: DashboardResource,
  ): Promise<DashboardResource> {
    const data = serializeDashboardResource(dashboard)

    return axios
      .put<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboard.id}`,
        { data },
        this.axiosConfig,
      )
      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async deleteDashboard(projectId: number, dashboardId: number): Promise<DashboardResource> {
    return axios
      .delete<Response<UnparsedDashboardResource>>(
        `${this.API_URL}/project/${projectId}/dashboard/${dashboardId}`,
        this.axiosConfig,
      )

      .then((response) => deserializeDashboardResource(response.data.data))
      .catch((error) => Promise.reject(error))
  }

  public async getChartData(chart: Chart): Promise<ChartDataProperties[]> {
    return axios
      .get<Response<ChartDataProperties[]>>(`${this.API_URL}${chart.path}`, this.axiosConfig)
      .then((response) => response.data.data)
      .catch((error) => Promise.reject(error))
  }

  public async getChartDetailsData(
    chart: Chart,
    kind: string,
    serverOptions: { page: number; rowsPerPage: number },
  ): Promise<ChartDetails> {
    const params = {
      kind,
      page: serverOptions.page,
      pageSize: serverOptions.rowsPerPage,
    }

    return axios
      .get<Response<any>>(`${this.API_URL}${chart.detailsPath}`, { ...this.axiosConfig, params })
      .then((response) => response.data.data)
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  public getChartDetailsHeaders(chartDetails: ChartDetails): AnalyzeTableHeader[] {
    const chartHeaders: AnalyzeTableHeader[] = []
    chartDetails.columns.forEach((currentColumn) => {
      const header: AnalyzeTableHeader = {
        title: currentColumn.id,
        // Here we need to remove the dots because our table framework can't handle dots as Header values.
        key: currentColumn.id.replace('.', '_').toLowerCase(),
        sortable: true,
      }
      if (!chartHeaders.find((currentHeader) => currentHeader.title === header.title)) {
        // Header did not exist in the headers list yet -> it will get added!
        chartHeaders.push(header)
      }
    })
    return chartHeaders
  }

  public getChartDetailsItems(chartDetails: ChartDetails): any[] {
    const chartHeaders: AnalyzeTableHeader[] = this.getChartDetailsHeaders(chartDetails)

    const dataTable: { [key: string]: string[] } = {}
    // Extract headers
    chartHeaders.forEach((column) => {
      if (column.key) dataTable[column.key] = []
    })

    // Check if 'id' key already exists using a safer method; if not, add it
    if (!Object.prototype.hasOwnProperty.call(dataTable, 'id')) {
      dataTable['id'] = []
    }

    // Map cell values to headers
    chartDetails.rows.forEach((row) => {
      if (row.cells[0].artifactId) dataTable['id'].push(row.cells[0].artifactId.toString())
      row.cells.forEach((cell, index) => {
        if (chartHeaders[index]) {
          const header = chartHeaders[index].key
          if (header) dataTable[header].push(cell.value)
        }
      })
    })

    const rows: any[] = []

    // Get the headers from the dataTable object
    const headers = Object.keys(dataTable)

    // Determine the maximum length among the arrays
    const maxLength = headers.reduce((max, header) => {
      return Math.max(max, dataTable[header].length)
    }, 0)

    // Iterate over the arrays and create objects for each row
    for (let i = 0; i < maxLength; i++) {
      const row: any = {}
      headers.forEach((header) => {
        row[header] = dataTable[header][i] || ''
      })
      rows.push(row)
    }

    return rows
  }

  public getMetricsDetailsPath(chart: Chart): string | undefined {
    return chart.detailsPath?.split('dashboard/')?.at(1)
  }
}
